import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'
import { Column } from 'primereact/column'
import type { DataTableExpandedRows, DataTableValueArray } from 'primereact/datatable'
import { exportUsers } from './api/usersApi'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import RefreshButton from '../../components/ui/RefreshButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { rowActionLabel } from '../../lib/deleteLabel'
import { userDisplayName } from './userDisplayName'
import { searchTerm } from '../../lib/searchTerm'
import { userColumns } from './components/UserColumns'
import UserRolesDialog from './components/UserRolesDialog'
import UserGroupsPanel from './components/UserGroupsPanel'
import { useUsers } from './hooks/useUsers'
import { useRoles } from './hooks/useRoles'
import type { RoleRef, UserWithRoles } from './types'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import './admin.css'

/**
 * Rol filtresindeki "Tüm roller" seçeneğinin değeri. Boş-OLMAYAN bir sentinel gerekir: PrimeReact
 * Dropdown, `null` değerli seçeneği "boş seçim" sayıp `onChange`'i yutar (`OperationCodesAdminPage`
 * `ALL_MODULES` ile aynı gerekçe). Rol id'leri daima pozitiftir → çakışmaz; API'ye `undefined` gider.
 */
const ALL_ROLES = -1

/** Admin → Kullanıcı yönetim ekranı (015). Listeleme/arama + rol filtresi + atanmış roller sütunu + rol atama (kullanıcı CRUD yok). */
export default function UsersAdminPage() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  // Kısıtlı Yetkili asistan (126): ekran salt okunur — durum switch'i pasif, rol/grup atama yok.
  const { readOnly } = useProxyMode()

  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  // ~400ms debounce (Page Standards).
  useEffect(() => {
    const id = setTimeout(() => setQ(searchInput), 400)
    return () => clearTimeout(id)
  }, [searchInput])

  // Rol filtresi: "bu role sahip kullanıcılar kim?" — sunucu yalnız role AKTİF bağı olanları döndürür;
  // satırdaki çipler filtrelenmez (kullanıcının tüm rolleri görünür). Excel dışa aktarım da aynı filtreyi alır.
  const [roleId, setRoleId] = useState<number>(ALL_ROLES)
  const roleFilter = roleId === ALL_ROLES ? undefined : roleId

  const { list, saveRoles, changeStatus } = useUsers(searchTerm(q), roleFilter)
  const roles = useRoles()
  const [rolesFor, setRolesFor] = useState<UserWithRoles | null>(null)
  // Kullanıcının grupları (122, DVA'daki gibi): masaüstünde satır açılır, dar ekranda kart içinde açılır.
  const [expanded, setExpanded] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined)
  const [groupsOpenId, setGroupsOpenId] = useState<number | null>(null)

  const rows = list.data?.content ?? []
  const activeRoles = (roles.list.data?.content ?? []).filter((r) => r.recordStatus === 1)
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const en = i18n.language.startsWith('en')
  const chipLabel = (r: RoleRef) => (en && r.nameEn ? r.nameEn : r.nameTr)
  const busyId = changeStatus.isPending ? (changeStatus.variables?.id ?? null) : null

  // Silinmemiş TÜM roller (pasif dahil): pasif bir rol kullanıcıda hâlâ çip olarak görünür, dolayısıyla
  // "kimde var?" sorusu onun için de sorulabilir. "Tüm roller" listenin başında.
  const roleOptions = useMemo(
    () => [
      { label: t('admin.users.filter.roleAll'), value: ALL_ROLES },
      ...(roles.list.data?.content ?? [])
        .map((r) => ({ label: en && r.nameEn ? r.nameEn : r.nameTr, value: r.id }))
        .sort((a, b) => a.label.localeCompare(b.label, en ? 'en' : 'tr')),
    ],
    [roles.list.data, en, t],
  )
  const hasFilter = searchInput.trim().length > 0 || roleId !== ALL_ROLES
  // `q` da ANINDA sıfırlanır: yalnız kutu temizlenseydi debounce dolana dek "eski arama + tüm roller"
  // için ara bir istek gider ve o sonuç kısa süre ekranda görünürdü.
  const clearAll = () => {
    setSearchInput('')
    setQ('')
    setRoleId(ALL_ROLES)
  }

  /**
   * Aktif/Pasif switch'i. `modDate` satırdan gönderilir (optimistic lock) — sunucu bir çakışma
   * görürse 409 döner ve merkezî hata toast'ı (019) devreye girer; ayrıca kendi kaydını ve sistem
   * kullanıcısını pasife çekme sunucuda reddedilir.
   */
  const onToggle = (row: UserWithRoles, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () =>
          toast.show({
            severity: 'success',
            summary: t('admin.users.toast.statusChanged'),
            life: 3000,
          }),
      },
    )
  }

  const onSaveRoles = (roleIds: number[]) => {
    if (!rolesFor) return
    saveRoles.mutate(
      { id: rolesFor.id, roleIds },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('admin.users.toast.rolesSaved'),
            life: 3000,
          })
          setRolesFor(null)
        },
        onError: () =>
          toast.show({ severity: 'error', summary: t('admin.users.errors.generic'), life: 3000 }),
      },
    )
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <UserRolesDialog
        visible={rolesFor != null}
        user={rolesFor}
        roleOptions={activeRoles}
        submitting={saveRoles.isPending}
        onSubmit={onSaveRoles}
        onHide={() => setRolesFor(null)}
      />

      <PageHeader
        title={t('nav.admin.users')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA — Figma tablo tasarimi (kullanici istegi 2026-08-26).
                Onceden filtre cubugundaydi (`FilterBar onRefresh`). */}
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton
              onExport={() => exportUsers(searchTerm(q), roleFilter)}
              disabled={rows.length === 0}
            />
          </>
        }
      />

      <FilterBar
        className="ui-filterbar--wide-search"
        searchValue={searchInput}
        searchPlaceholder={t('admin.users.search')}
        onSearch={setSearchInput}
        showClear={hasFilter}
        clearLabel={t('admin.users.filter.clear')}
        onClear={clearAll}
      >
        {/* "Tüm roller" açık bir seçenek → `showClear` (X) yok. Rol adları uzun ve sayıca çok olabildiği
            için listede arama açık (TR-farkında). aria-label kontrolün ne olduğunu söyler. */}
        <Dropdown
          className="admin-role-filter"
          value={roleId}
          options={roleOptions}
          onChange={(e) => setRoleId((e.value as number | null) ?? ALL_ROLES)}
          aria-label={t('admin.users.filter.roleLabel')}
          // Veri yokken (ilk yükleme / ilk yükleme hatası) kapalı. `isError`'a BAĞLANMAZ (RolesAdminPage
          // yetki filtresiyle aynı gerekçe): React Query refetch hatasında eski veriyi tutup isError=true
          // yapar — liste filtreli kalırken seçici kilitlenir ve seçim değiştirilemezdi.
          disabled={!roles.list.data}
          filter
          // Panelin İÇİNDEKİ arama kutusu ayrı bir <input>'tur ve yukarıdaki `aria-label`'dan
          // ad ALMAZ — o, Dropdown tetikleyicisine aittir. Adsız bırakılırsa ekran okuyucu
          // kullanıcısı kutunun ne aradığını duymaz (erişilebilirlik incelemesi 19.10.2026).
          // Yol passthrough'dur (`UserMultiPicker`'daki `ariaPt` ile aynı kalıp): PrimeReact
          // bileşene verilen prop'u KÖKE koyar, iç girdiye `pt` ile ulaşılır. `filterPlaceholder`
          // tercih edilmedi — yer tutucu yazmaya başlayınca kaybolur, kalıcı ad vermez.
          pt={{ filterInput: { 'aria-label': t('admin.users.filter.roleSearch') } }}
          filterLocale={en ? 'en' : 'tr'}
          scrollHeight="28rem"
        />
      </FilterBar>

      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && rows.length === 0 && (
            <EmptyState
              title={t('admin.users.empty.title')}
              description={t('admin.users.empty.desc')}
            />
          )}
          {!list.isLoading &&
            rows.map((row) => (
              <div key={row.id} className="ui-card">
                {/* `ui-card-top` yerleşimi ReorderableLookupCards ile AYNI: solda durum rozeti,
                    sağda satır işlemleri. Rol çipleri buradan aşağıdaki içerik satırına indi —
                    çip bir "durum" değil, kartın içeriğidir ve üst şeridin sol yuvası rozetin. */}
                <div className="ui-card-top">
                  <Badge tone={row.recordStatus === 1 ? 'green' : 'neutral'}>
                    {row.recordStatus === 1
                      ? t('admin.users.status.active')
                      : t('admin.users.status.passive')}
                  </Badge>
                  <div className="ui-card-actions ui-card-actions--compact">
                    {/* Kart görünümü sunumu değiştirir, işlemleri DEĞİL (components/ui/README.md
                        "Dar ekran kart görünümü") → aç/kapa switch'i tabloda olduğu gibi burada da var. */}
                    <InputSwitch
                      className="admin-switch"
                      checked={row.recordStatus === 1}
                      disabled={readOnly || busyId === row.id}
                      onChange={(e) => onToggle(row, Boolean(e.value))}
                      aria-label={`${row.name} ${row.surname}`}
                      tooltip={t('admin.users.actions.toggle')}
                      tooltipOptions={{ position: 'bottom' }}
                    />
                    {/* Salt okunur kipte Roller düğmesi yok: roller aşağıda çip olarak zaten okunuyor. */}
                    {!readOnly && (
                      <Button
                        icon="pi pi-users"
                        rounded
                        text
                        label={t('admin.users.actions.roles')}
                        aria-label={rowActionLabel(t, 'admin.users.actions.roles', userDisplayName(row))}
                        onClick={() => setRolesFor(row)}
                      />
                    )}
                    <Button
                      icon="pi pi-sitemap"
                      rounded
                      text
                      label={t('admin.users.actions.groups')}
                      aria-label={rowActionLabel(t, 'admin.users.actions.groups', userDisplayName(row))}
                      aria-expanded={groupsOpenId === row.id}
                      onClick={() => setGroupsOpenId(groupsOpenId === row.id ? null : row.id)}
                    />
                  </div>
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">
                    {userDisplayName(row)}
                  </span>
                </div>
                <div className="ui-card-row">
                  {row.userName && <span>{row.userName}</span>}
                  {row.email && <span>{row.email}</span>}
                </div>
                {(row.roles ?? []).length > 0 && (
                  <div className="admin-role-chips">
                    {(row.roles ?? []).map((r) => (
                      <Badge key={r.id} tone="neutral">
                        {chipLabel(r)}
                      </Badge>
                    ))}
                  </div>
                )}
                {(row.groups ?? []).length > 0 && (
                  <div className="admin-role-chips">
                    {(row.groups ?? []).map((g) => (
                      <Badge key={g.id} tone="neutral">
                        {chipLabel(g)}
                      </Badge>
                    ))}
                  </div>
                )}
                {groupsOpenId === row.id && <UserGroupsPanel user={row} readOnly={readOnly} />}
              </div>
            ))}
        </div>
      ) : (
      <DataTable
        value={rows}
        loading={list.isLoading}
        dataKey="id"
        sortField="name"
        sortOrder={1}
        expandedRows={expanded}
        onRowToggle={(e) => setExpanded(e.data)}
        rowExpansionTemplate={(row: UserWithRoles) => (
          <UserGroupsPanel user={row} readOnly={readOnly} />
        )}
        emptyMessage={
          <EmptyState
            title={t('admin.users.empty.title')}
            description={t('admin.users.empty.desc')}
          />
        }
      >
        {/* Satır açma oku: kullanıcının grupları (Grup Yönetimi'ndeki dar ok sütunuyla aynı). */}
        <Column
          expander
          headerClassName="admin-expander-col"
          bodyClassName="admin-expander-col"
          style={{ width: '2.25rem' }}
        />
        {userColumns({ t, en, busyId, onRoles: setRolesFor, onToggle, readOnly })}
      </DataTable>
      )}
    </div>
  )
}
